"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Checkbox } from "./ui/checkbox"
import { AlertTriangle, Download, EyeOff, Loader2, Lock, ShieldAlert } from "lucide-react"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"

/**
 * Workflow-hub data-export modal. Driven by the active grid state (filter,
 * stage, visible columns); produces an .xlsx via POST /api/workflow/v2/export
 * with one sheet per selected stage. Phase 1 scope:
 *   - default column set = current view's visible columns, with a "show all"
 *     toggle that surfaces every authorized field in the configuration
 *   - row-count preview per selected stage (cheap top=0 search)
 *   - editable filename with a generic default (no tenant / worker info)
 *   - inline warning banners for client-facing-off OR private columns
 *   - cap-overflow path renders the server's 413 body as a "narrow the filter"
 *     prompt instead of a generic error toast
 *
 * Multi-stage support: the modal opens with the current stage pre-selected;
 * the user can add other stages (same entityType) and each becomes its own
 * sheet. Column set is shared across sheets — the server applies it uniformly
 * and falls back to blank cells for any field that doesn't exist on a given
 * row, matching the FE's "you picked these columns" mental model.
 */

interface VisibleStage {
  stageCode: string
  stageTitle: string
  /**
   * Stage code to pass to <c>searchWorkflowItems</c>' first positional arg
   * for the count probe. <c>undefined</c> when the stage has a fixedFilter
   * (Tasks tab, VMS sub-tabs) — for those, the complete filter is in
   * <see cref="customFilters"/> and <c>searchWorkflowItems</c> skips its
   * default <c>Stage eq '…'</c> injection.
   */
  searchStage?: string
  /**
   * Per-stage filter clauses (joined-OData strings) that the count probe
   * passes as <c>searchWorkflowItems</c>' customFilter argument. For
   * WorkOrder-shaped stages this is <c>['Stage eq ''X'' and ...userFilters']</c>
   * (one element holding the joined clauses), so
   * <c>buildFilterFromStageAndFilters</c> can prepend
   * <c>DocumentType eq 'Assignment'</c>. For VMS / fixedFilter stages the
   * array carries the complete filter (already including any DocumentType
   * discriminator the BE expects).
   */
  customFilters?: string[]
  /**
   * The fully-built OData filter string sent to the EXPORT endpoint
   * (POST /api/workflow/v2/export). Distinct from <see cref="customFilters"/>
   * because the export endpoint doesn't run through
   * <c>buildFilterFromStageAndFilters</c> — the FE is responsible for the
   * complete filter including <c>DocumentType eq 'Assignment'</c> for
   * WorkOrder-shaped stages. For VMS the BE adds <c>DocumentType eq
   * 'VmsRequest' / 'VmsEmail'</c> server-side, so this can be just the
   * user filters.
   */
  exportFilter?: string
  orderBy?: string
  /** Suppresses any global filters the user has applied (matches stage config). */
  excludeGlobalFilters?: boolean
  /**
   * Visible-on-the-grid columns for THIS stage (what the user would see
   * if they navigated to it). The modal unions these across selected
   * stages so picking Extension + Onboarding shows columns from both —
   * otherwise the picker would only surface the active-stage subset and
   * Onboarding-only columns would be invisible.
   */
  visibleColumns?: FieldConfig[]
}

interface FieldConfig {
  fieldConfigurationId: string
  columnCode?: string | null
  columnNameEnglish?: string | null
  columnNameFrench?: string | null
  isPrivate?: boolean | null
  /** Field-config flag: false = "this column carries data not intended for clients". */
  isEnabledForClients?: boolean | null
}

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  /** Entity type backing the active grid (WorkOrder / VmsRequest / etc.). */
  entityType: string
  /** Stage the user is currently on — pre-selected and required. */
  activeStage: VisibleStage
  /**
   * All stages of the active entity that the user could ADD to the export.
   * Doesn't include the active stage itself (it's always selected). For
   * single-stage tabs (VMS / ATS) this list is empty.
   */
  otherStages: VisibleStage[]
  /**
   * Columns currently visible on the grid in the order they appear left-to-
   * right. Used as the default selection — most users will export "what they
   * see" and click straight through.
   */
  visibleColumns: FieldConfig[]
  /**
   * Complete authorized field set — anything the user could legally pick if
   * they hit "Show all columns". Pre-filtered by field-level security at the
   * API layer.
   */
  allAuthorizedColumns: FieldConfig[]
  /** "en" or "fr" — forwarded to the server for header localisation. */
  language?: string
}

export function ExportModal({
  isOpen,
  onClose,
  entityType,
  activeStage,
  otherStages,
  visibleColumns,
  allAuthorizedColumns,
  language,
}: ExportModalProps) {
  const { t } = useTranslation()
  const { searchWorkflowItems, exportWorkflowItems } = useWorkflowApi()
  const { toast } = useToast()

  // ── Stage selection ───────────────────────────────────────────────────
  // Set of stage codes the user wants in the export. The active stage is
  // pre-selected; the user can tick additional stages or uncheck the
  // active one (as long as at least one stage stays selected — single-
  // sheet exports are still valid).
  const [selectedStageCodes, setSelectedStageCodes] = useState<Set<string>>(
    () => new Set([activeStage.stageCode])
  )

  // Which stage's columns the picker currently shows. Click a stage pill
  // body to swap this — the "Columns" section retitles and re-lists.
  // Persisted independently of selectedStageCodes so the user can switch
  // tabs without unchecking.
  const [activeStageInPicker, setActiveStageInPicker] = useState<string>(activeStage.stageCode)

  // Per-stage column selection. Each entry is the ordered list of
  // fieldConfigurationIds the user picked for that stage. The export
  // sends each sheet its own list — the model the BE migrated to in
  // ExportSheetDto.FieldConfigurationIds. Default for the active stage
  // is its visible-columns list; default when a new stage is ADDED is
  // that stage's own visible-columns list.
  const [selectedColumnsByStage, setSelectedColumnsByStage] = useState<Record<string, string[]>>(
    () => ({
      [activeStage.stageCode]: (activeStage.visibleColumns ?? visibleColumns)
        .map(c => c.fieldConfigurationId),
    })
  )

  // ── Column-list view mode ─────────────────────────────────────────────
  // "visible" (default) shows the picked stage's visibleColumns; "all"
  // expands to every authorized field. Independent of selection state.
  const [showAllColumns, setShowAllColumns] = useState(false)

  // Reset state on modal open. Falls back to the active stage being
  // pre-selected with its visible-columns set.
  useEffect(() => {
    if (isOpen) {
      setSelectedStageCodes(new Set([activeStage.stageCode]))
      setActiveStageInPicker(activeStage.stageCode)
      setSelectedColumnsByStage({
        [activeStage.stageCode]: (activeStage.visibleColumns ?? visibleColumns)
          .map(c => c.fieldConfigurationId),
      })
      setShowAllColumns(false)
      setFilename(buildDefaultFilename(activeStage))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeStage.stageCode])

  // ── Filename ──────────────────────────────────────────────────────────
  // Default = {StageTitle}_{YYYY-MM-DD}. No tenant / client / worker info.
  // Editable; sanitised server-side.
  const [filename, setFilename] = useState(buildDefaultFilename(activeStage))

  // ── Row count preview ─────────────────────────────────────────────────
  // Re-fired when the stage selection changes. Each selected stage gets a
  // separate top=0+count=true probe so the modal can show per-stage counts
  // alongside the total.
  //
  // Strict-mode safety: React 18's StrictMode dev wrapper double-invokes
  // effects (mount → unmount → remount). Without the lastFetchedKeyRef
  // guard the count probes fire twice for the same stage set on every
  // open, which the user sees as duplicate search calls in the network
  // tab. The ref tracks "stage set we already kicked off probes for" —
  // re-running the effect with the same key is a no-op, but a real
  // selection change (stage toggle) busts the key and probes refire.
  const [countsByStage, setCountsByStage] = useState<Record<string, number | "loading" | "error">>({})

  // All stages the user could pick — active first, then the others in
  // declaration order. Used both for rendering the stage pill row and
  // for selecting the picker focus.
  const allStages = useMemo<VisibleStage[]>(
    () => [activeStage, ...otherStages],
    [activeStage, otherStages]
  )

  // Stages selected for export, preserving the active-first ordering.
  // The workbook builds sheets in this order.
  const selectedStages = useMemo<VisibleStage[]>(() => {
    return allStages.filter(s => selectedStageCodes.has(s.stageCode))
  }, [allStages, selectedStageCodes])

  const lastFetchedKeyRef = useRef<string>("")

  useEffect(() => {
    if (!isOpen) {
      // Reset the guard on close so reopening the modal does fire a
      // fresh probe — counts could be stale (someone created / closed
      // requests in another tab while this one was idle).
      lastFetchedKeyRef.current = ""
      return
    }

    const key = selectedStages.map(s => s.stageCode).join("|")
    if (key === lastFetchedKeyRef.current) return
    lastFetchedKeyRef.current = key

    // No `cancelled` flag here, deliberately. Earlier this used the
    // cleanup-sets-cancelled-true pattern to drop stale writes, but it
    // interacted badly with React 18 StrictMode: the first mount's fetch
    // gets cancelled by the immediate-strict-unmount cleanup, the remount
    // hits the ref guard and skips re-fetching, so the resolved fetch's
    // setState was suppressed and the row count stuck on "loading"
    // forever. Letting writes through means at worst a transient
    // race when the user rapidly toggles stages (old fetch overwrites a
    // new one), which is acceptable for a count badge — the next stable
    // selection refires the fetches.
    const fetchCount = async (stage: VisibleStage) => {
      setCountsByStage(prev => ({ ...prev, [stage.stageCode]: "loading" }))
      try {
        // Pass the stage code (positional 1) + the per-stage user filters
        // (positional 2 customFilter). searchWorkflowItems' built-in
        // buildFilterFromStageAndFilters then prepends `DocumentType eq
        // 'Assignment'` for WorkOrder-shaped stages — without that prefix
        // the search index narrows to zero rows (you'd see count=0 even
        // when the grid happily shows rows for the same stage). Passing
        // the pre-built filter via positional 8 bypassed that helper, which
        // was the bug.
        //
        // VMS sub-tabs have stage.searchStage=undefined (they use a
        // fixed-filter pattern) — for those, customFilter carries the
        // complete filter including the DocumentType clause the BE
        // expects, since buildFilterFromStageAndFilters skips the
        // Assignment prefix for VMS entity types.
        //
        // SELECT="id" — count probes don't materialise rows (top=0), but
        // searchWorkflowItems falls back to a long default SELECT list
        // when callers pass undefined. Explicitly asking for just `id`
        // keeps the request payload small and stops the network tab from
        // showing a confusing "why is the export asking for these fields?"
        // shape that doesn't match the user's modal selection.
        const result = await searchWorkflowItems(
          stage.searchStage as any,
          stage.customFilters && stage.customFilters.length > 0 ? stage.customFilters : undefined,
          [],
          1, 0, '*',
          'id',                 // <-- minimal SELECT for count-only probes
          undefined,            // <-- filter positional-8 left undefined so buildFilterFromStageAndFilters runs
          stage.orderBy,
        )
        setCountsByStage(prev => ({ ...prev, [stage.stageCode]: Number(result?.totalCount) || 0 }))
      } catch {
        setCountsByStage(prev => ({ ...prev, [stage.stageCode]: "error" }))
      }
    }

    selectedStages.forEach(fetchCount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedStages.map(s => s.stageCode).join("|")])

  const totalRowCount = useMemo(() => {
    let sum = 0
    let anyLoading = false
    for (const stage of selectedStages) {
      const c = countsByStage[stage.stageCode]
      if (c === "loading") { anyLoading = true; continue }
      if (typeof c === "number") sum += c
    }
    return { sum, anyLoading }
  }, [selectedStages, countsByStage])

  // ── Warnings: client-facing-off + private ─────────────────────────────
  // The authoritative source for `isEnabledForClients` / `isPrivate` is
  // `allAuthorizedColumns` (from the tenant configuration's fieldsInUse).
  // The grid-supplied `visibleColumns` is a stage-scoped projection where
  // those flags can be stripped / defaulted, so we look up every column
  // we render or count through this map. Without this normalisation the
  // not-client-facing icon rendered on EVERY row regardless of the actual
  // flag, because the visible-columns shape happened to have
  // isEnabledForClients undefined or default-false everywhere.
  const authoritativeColumnsById = useMemo(() => {
    return new Map(allAuthorizedColumns.map(c => [c.fieldConfigurationId, c]))
  }, [allAuthorizedColumns])

  // Whenever a column might be rendered or counted, route through this
  // helper so the flags are the ones the user really cares about.
  const resolveColumn = (col: FieldConfig): FieldConfig => {
    const authoritative = authoritativeColumnsById.get(col.fieldConfigurationId)
    if (!authoritative) return col
    return {
      ...col,
      // Names from the rendered side, flags from the authoritative side.
      // Names match what the user sees in the grid; flags match what the
      // backend actually persists for security / client-share decisions.
      isPrivate: authoritative.isPrivate ?? col.isPrivate,
      isEnabledForClients: authoritative.isEnabledForClients ?? col.isEnabledForClients,
    }
  }

  // Aggregated selected columns across all stages — used by the warning
  // banners to count not-client-facing / private selections in the whole
  // export, not just one stage. Deduped by columnId so a column picked
  // on two stages doesn't double-count in the warning text.
  const aggregatedSelectedColumns = useMemo(() => {
    const seen = new Set<string>()
    const out: FieldConfig[] = []
    for (const stage of selectedStages) {
      const ids = selectedColumnsByStage[stage.stageCode] ?? []
      for (const id of ids) {
        if (seen.has(id)) continue
        const col = authoritativeColumnsById.get(id)
        if (col) {
          seen.add(id)
          out.push(col)
        }
      }
    }
    return out
  }, [selectedStages, selectedColumnsByStage, authoritativeColumnsById])

  const notClientFacing = aggregatedSelectedColumns.filter(c => c.isEnabledForClients === false)
  const privateColumns = aggregatedSelectedColumns.filter(c => c.isPrivate === true)

  // Quick action: drop every selected column that's flagged not-client-
  // facing OR private across ALL stages in one click. Replaces the
  // per-stage "uncheck them one at a time" flow.
  const hasInternalSelections = useMemo(() => {
    return aggregatedSelectedColumns.some(
      c => c.isEnabledForClients === false || c.isPrivate === true
    )
  }, [aggregatedSelectedColumns])

  const internalSelectedCount = useMemo(() => {
    return aggregatedSelectedColumns.filter(
      c => c.isEnabledForClients === false || c.isPrivate === true
    ).length
  }, [aggregatedSelectedColumns])

  const excludeInternalColumns = () => {
    if (!hasInternalSelections) return
    setSelectedColumnsByStage(prev => {
      const next: Record<string, string[]> = {}
      for (const [stageCode, ids] of Object.entries(prev)) {
        next[stageCode] = ids.filter(id => {
          const col = authoritativeColumnsById.get(id)
          if (!col) return true
          return col.isEnabledForClients !== false && col.isPrivate !== true
        })
      }
      return next
    })
  }

  // ── Per-stage helpers used by the JSX below ───────────────────────────
  // The column picker shows columns from THIS stage and toggling a
  // checkbox updates THIS stage's selection. Stage pill checkboxes use
  // toggleStageInclusion; pill body click swaps the picker focus via
  // focusPickerOn (and adds the stage if it wasn't already in the set).
  const activeStageData = useMemo(
    () => allStages.find(s => s.stageCode === activeStageInPicker) ?? activeStage,
    [allStages, activeStageInPicker, activeStage]
  )
  const activeStageSelection = selectedColumnsByStage[activeStageInPicker] ?? []

  const toggleColumn = (id: string) => {
    setSelectedColumnsByStage(prev => {
      const current = prev[activeStageInPicker] ?? []
      const next = current.includes(id)
        ? current.filter(x => x !== id)
        : [...current, id]
      return { ...prev, [activeStageInPicker]: next }
    })
  }

  const toggleStageInclusion = (code: string) => {
    setSelectedStageCodes(prev => {
      const next = new Set(prev)
      if (next.has(code)) {
        // Refuse to uncheck the last stage — export needs at least one.
        if (next.size === 1) return prev
        next.delete(code)
        // If the user just unchecked the stage the picker was focused
        // on, jump focus to whatever's left selected so the column list
        // doesn't display an empty / phantom stage.
        if (code === activeStageInPicker) {
          const remaining = allStages.find(s => next.has(s.stageCode))
          if (remaining) setActiveStageInPicker(remaining.stageCode)
        }
      } else {
        next.add(code)
        // Seed the newly-added stage's column selection with its own
        // visibleColumns — mirrors the "default = what you see"
        // intuition that powers the single-stage default.
        setSelectedColumnsByStage(prevCols => {
          if (prevCols[code]) return prevCols
          const stage = allStages.find(s => s.stageCode === code)
          const defaultCols = (stage?.visibleColumns ?? []).map(c => c.fieldConfigurationId)
          return { ...prevCols, [code]: defaultCols }
        })
      }
      return next
    })
  }

  const focusPickerOn = (code: string) => {
    // Clicking a pill body always brings the picker to that stage. If
    // the stage wasn't selected yet, add it too — the click reads as
    // "I want to configure this stage's columns," and the user would
    // need it ticked anyway for that to matter.
    if (!selectedStageCodes.has(code)) {
      toggleStageInclusion(code)
    }
    setActiveStageInPicker(code)
  }

  // ── Submit ────────────────────────────────────────────────────────────
  const [isExporting, setIsExporting] = useState(false)
  const [overLargeBody, setOverLargeBody] = useState<{ rowCount: number; syncCap: number; message: string } | null>(null)

  // Every selected stage must have at least one column picked — the BE
  // rejects empty-column sheets with a clear error, but it's better UX
  // to disable the Export button up front.
  const everyStageHasColumns = selectedStages.every(
    s => (selectedColumnsByStage[s.stageCode]?.length ?? 0) > 0
  )
  const canExport = selectedStages.length > 0 && everyStageHasColumns && !isExporting

  const handleExport = async () => {
    if (!canExport) return
    setIsExporting(true)
    setOverLargeBody(null)
    // Send the grid's own header labels for the columns being exported so the .xlsx headers match the grid.
    // Client custom-field relabels (the "purple" columns) live only in browser state, so without this the
    // server falls back to the generic column name. Non-relabeled columns carry their generic label here too,
    // so they're no-ops server-side.
    const exportedColumnIds = new Set(
      selectedStages.flatMap(s => selectedColumnsByStage[s.stageCode] ?? [])
    )
    const columnLabelOverrides = allAuthorizedColumns
      .filter(c => exportedColumnIds.has(c.fieldConfigurationId))
      .map(c => ({
        fieldConfigurationId: c.fieldConfigurationId,
        columnNameEnglish: c.columnNameEnglish ?? undefined,
        columnNameFrench: c.columnNameFrench ?? undefined,
      }))
    try {
      const result = await exportWorkflowItems({
        entityType,
        sheets: selectedStages.map(s => ({
          stageCode: s.stageCode,
          stageTitle: s.stageTitle,
          // exportFilter is the FULLY-BUILT OData (host computed it
          // including any DocumentType discriminator the BE expects). The
          // BE export endpoint doesn't re-derive the filter the way
          // searchWorkflowItems does — what we send here is what runs.
          filter: s.exportFilter,
          orderBy: s.orderBy,
          // Per-sheet column selection. The BE uses sheet.FieldConfigurationIds
          // when set, otherwise falls back to the request-level default
          // (request.fieldConfigurationIds). We always send per-sheet now
          // for the multi-stage UX, but pass an empty default list so
          // older request handlers wouldn't break.
          fieldConfigurationIds: selectedColumnsByStage[s.stageCode] ?? [],
        })),
        fieldConfigurationIds: [],
        columnLabelOverrides,
        filename: filename.trim() || undefined,
        language: language,
      })

      if (result.ok) {
        // Trigger the browser download via an object URL. Revoke it on
        // next tick so the link doesn't leak; Chrome / Firefox / Safari
        // all accept the click before the URL is freed.
        const url = URL.createObjectURL(result.blob)
        const link = document.createElement('a')
        link.href = url
        link.download = result.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(url), 0)
        toast({ variant: 'success', title: t('success'), description: result.filename })
        onClose()
        return
      }

      // 413 over-cap: surface a structured banner inside the modal so the
      // user can adjust the scope without losing their column / stage picks.
      if (result.status === 413 && result.body && typeof result.body === 'object') {
        setOverLargeBody({
          rowCount: Number(result.body.rowCount) || 0,
          syncCap: Number(result.body.syncCap) || 10000,
          message: String(result.body.message ?? t('exportTooLarge')),
        })
        return
      }

      // 403 = ExportData missing on the server side (shouldn't happen if the
      // FE button gate is honoured, but defensive for hand-crafted requests).
      // 400 = validation failure. Surface both as toasts since they're not
      // fixable by tweaking the modal's inputs.
      toast({
        variant: 'destructive',
        title: t('error'),
        description: typeof result.body === 'string'
          ? result.body
          : (result.body?.error || result.body?.message || t('exportServerError').replace('{status}', String(result.status))),
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: t('error'),
        description: err instanceof Error ? err.message : t('exportUnexpectedError'),
      })
    } finally {
      setIsExporting(false)
    }
  }

  // (toggleColumn / toggleStageInclusion / focusPickerOn are declared
  // above in the per-stage helpers block — kept there because their
  // behaviour reads off state that's introduced earlier in the file.)

  const labelFor = (c: FieldConfig) => {
    const useFrench = (language || 'en').toLowerCase() === 'fr'
    return useFrench
      ? (c.columnNameFrench || c.columnNameEnglish || c.columnCode || c.fieldConfigurationId)
      : (c.columnNameEnglish || c.columnNameFrench || c.columnCode || c.fieldConfigurationId)
  }

  // Columns displayed in the picker. Switches per the user's currently-
  // focused stage (activeStageData) rather than rendering a union — the
  // per-stage model means each stage owns its own list, and the picker
  // is a single-stage editor that you focus by clicking pills.
  //
  // Always run through resolveColumn so icons reflect the authoritative
  // isEnabledForClients / isPrivate flags from fieldsInUse — not whatever
  // shape the stage-scoped visibleColumns happened to carry.
  const pickerStageVisibleColumns = activeStageData.visibleColumns
    ?? (activeStageData.stageCode === activeStage.stageCode ? visibleColumns : [])
  const columnsToList = (showAllColumns ? allAuthorizedColumns : pickerStageVisibleColumns).map(resolveColumn)

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isExporting) onClose() }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-900">
            <Download size={18} />
            {t('exportToExcel')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2 max-h-[60vh] overflow-y-auto pr-1">
          {/* ── Scope (selected stages summary) ──────────────────────────
              Read-only list of selected stages with per-stage row counts.
              Updates live as the user toggles pills below. Mirrors the
              earlier Scope shape so the user always has a clear "what
              will the workbook contain?" snapshot at the top of the
              modal. */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('exportScope')}</h3>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 space-y-1.5">
              {selectedStages.map(stage => {
                const count = countsByStage[stage.stageCode]
                const isActive = stage.stageCode === activeStage.stageCode
                return (
                  <div key={stage.stageCode} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">
                      {stage.stageTitle}
                      {isActive && (
                        <span className="ml-1.5 text-xs text-gray-400">({t('current')})</span>
                      )}
                    </span>
                    <span className="text-gray-500 text-xs">
                      {count === 'loading' ? (
                        <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> …</span>
                      ) : count === 'error' ? (
                        <span className="text-red-500">—</span>
                      ) : (
                        <>{(count ?? 0).toLocaleString()} {t('rows')}</>
                      )}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* Stage pills — every stage of the entity, simple checkbox +
                name. Click the pill body to focus the column picker on
                that stage; the active picker stage gets a blue ring so
                the user always knows which stage's columns the section
                below corresponds to. No row / column counts here — those
                live up in the Scope summary; keeping the pills lean
                makes the multi-stage selection visually quick to scan. */}
            <div className="mt-3">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">
                {t('stages')}
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {allStages.map(stage => {
                  const picked = selectedStageCodes.has(stage.stageCode)
                  const isFocused = stage.stageCode === activeStageInPicker
                  return (
                    <div
                      key={stage.stageCode}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors cursor-pointer
                        ${picked ? 'bg-white border-gray-300 text-gray-800' : 'bg-gray-50 border-gray-200 text-gray-500'}
                        ${isFocused ? 'ring-2 ring-blue-300 border-blue-300' : ''}
                        hover:bg-gray-100`}
                      onClick={() => focusPickerOn(stage.stageCode)}
                      role="button"
                      tabIndex={0}
                    >
                      <Checkbox
                        checked={picked}
                        onCheckedChange={() => toggleStageInclusion(stage.stageCode)}
                        // Stop propagation so the click doesn't ALSO
                        // trigger focusPickerOn — toggling and focusing
                        // are separate intents.
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="font-medium">{stage.stageTitle}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>

          {/* ── Columns (per-stage) ────────────────────────────────────
              Title retitles based on which stage the picker is focused
              on so the user always knows what they're editing. The
              column count after the title reflects ONLY the currently-
              focused stage's selection — the per-stage chip badge
              already showed all stages' counts above. */}
          <section>
            <div className="flex items-center justify-between mb-2 gap-3">
              <h3 className="text-sm font-semibold text-gray-700 shrink-0">
                {t('exportColumnsForStage').replace('{stage}', activeStageData.stageTitle)}{' '}
                <span className="text-gray-400 font-normal">({activeStageSelection.length})</span>
              </h3>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                {/* "Sanitise for sharing" quick action. Acts across ALL
                    stages — strips not-client-facing AND private columns
                    from every stage's selection in one click. Count
                    reflects the aggregate (deduped) so the user sees a
                    truthful "this will remove N columns from your
                    selections" number rather than a per-stage one. */}
                {hasInternalSelections && (
                  <button
                    type="button"
                    onClick={excludeInternalColumns}
                    className="text-xs text-amber-700 hover:underline inline-flex items-center gap-1"
                  >
                    <ShieldAlert size={12} />
                    {t('exportExcludeInternal').replace('{count}', String(internalSelectedCount))}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowAllColumns(v => !v)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {showAllColumns
                    ? t('exportShowVisibleOnly')
                    : t('exportShowAllColumns')}
                </button>
              </div>
            </div>

            <div className="rounded-md border border-gray-200 max-h-56 overflow-y-auto">
              {columnsToList.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-500 italic">
                  {t('exportNoColumns')}
                </div>
              ) : (
                <ul>
                  {columnsToList.map(col => {
                    const checked = activeStageSelection.includes(col.fieldConfigurationId)
                    return (
                      <li
                        key={col.fieldConfigurationId}
                        className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                      >
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 flex-1">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleColumn(col.fieldConfigurationId)}
                          />
                          <span>{labelFor(col)}</span>
                        </label>
                        <div className="flex items-center gap-1">
                          {/* EyeOff (amber) = "won't be visible to clients" —
                              clearer than the old Users icon, which read as
                              "people = clients = client-facing" and got
                              everyone backwards. */}
                          {col.isEnabledForClients === false && (
                            <EyeOff size={13} className="text-amber-500" aria-label={t('exportNotClientFacing')} />
                          )}
                          {col.isPrivate === true && (
                            <Lock size={13} className="text-red-500" aria-label={t('exportPrivateColumn')} />
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </section>

          {/* ── Warnings ─────────────────────────────────────────────── */}
          {(notClientFacing.length > 0 || privateColumns.length > 0) && (
            <section className="space-y-2">
              {notClientFacing.length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-md border border-amber-200 bg-amber-50 text-sm">
                  <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-amber-800">
                    <strong>{notClientFacing.length}</strong>{' '}
                    {t('exportWarnNotClientFacingCount')}
                    <span className="text-amber-700"> ({notClientFacing.map(labelFor).join(', ')}).</span>{' '}
                    {t('exportWarnNotClientFacingBody')}
                  </div>
                </div>
              )}
              {privateColumns.length > 0 && (
                <div className="flex items-start gap-2 p-3 rounded-md border border-red-200 bg-red-50 text-sm">
                  <Lock size={16} className="text-red-600 shrink-0 mt-0.5" />
                  <div className="text-red-800">
                    <strong>{privateColumns.length}</strong>{' '}
                    {t('exportWarnPrivateCount')}
                    <span className="text-red-700"> ({privateColumns.map(labelFor).join(', ')}).</span>{' '}
                    {t('exportWarnPrivateBody')}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ── Over-cap server response ────────────────────────────── */}
          {overLargeBody && (
            <section className="p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-800">
              <div className="font-semibold flex items-center gap-1.5 mb-1">
                <AlertTriangle size={14} /> {t('exportTooLarge')}
              </div>
              <p>{overLargeBody.message}</p>
            </section>
          )}

          {/* ── Filename ─────────────────────────────────────────────── */}
          <section>
            <Label className="text-sm text-gray-700">{t('exportFilename')}</Label>
            <Input
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="export.xlsx"
              className="mt-1 font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('exportFilenameHint')}
            </p>
          </section>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-gray-100">
          <Button variant="elliptical-outline" onClick={onClose} disabled={isExporting}>
            {t('cancel')}
          </Button>
          <Button
            variant="elliptical"
            onClick={handleExport}
            disabled={!canExport}
            className="flex items-center gap-2"
          >
            {isExporting ? (
              <><Loader2 size={14} className="animate-spin" /> {t('exporting')}</>
            ) : (
              <>
                <Download size={14} />
                {t('exportRowsButton').replace('{count}', totalRowCount.sum.toLocaleString())}
                {totalRowCount.anyLoading && <Loader2 size={12} className="animate-spin opacity-60" />}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Generic default filename — deliberately omits any tenant / client / worker
 * identifier so a downloaded file can't leak organisation info via its name.
 * The user can fully rewrite this in the modal before triggering the export.
 */
function buildDefaultFilename(stage: { stageTitle: string }): string {
  const safe = (stage.stageTitle || 'Export').replace(/[\\/:*?"<>|]/g, '').trim() || 'Export'
  const date = new Date().toISOString().slice(0, 10) // YYYY-MM-DD in UTC — terse and locale-neutral
  return `${safe}_${date}.xlsx`
}
